import { useEffect, useMemo, useRef, useState } from "react";
import { Move, ZoomIn, ZoomOut } from "lucide-react";
import {
  AVATAR_OUTPUT_SIZE,
  MIN_CROP_SOURCE_SIZE,
  clampCropPosition,
  cropSourceRect,
  cropZoomLimit,
} from "@/lib/profileCrop";

const DEFAULT_VIEWPORT = 320;

export default function ProfilePhotoCropper({ file, onCancel, onConfirm }) {
  const [image, setImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const preview = useRef(null);
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);

  useEffect(() => {
    if (!preview.current) return undefined;
    setViewport(preview.current.clientWidth || DEFAULT_VIEWPORT);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setViewport(entry.contentRect.width || DEFAULT_VIEWPORT));
    observer.observe(preview.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const nextImage = new Image();
    nextImage.onload = () => {
      if (Math.min(nextImage.naturalWidth, nextImage.naturalHeight) < MIN_CROP_SOURCE_SIZE) {
        setError(`Billedet skal være mindst ${MIN_CROP_SOURCE_SIZE} pixels på den korteste side.`);
        URL.revokeObjectURL(url);
        return;
      }
      setImage(nextImage);
    };
    nextImage.onerror = () => {
      setError("Billedet kunne ikke åbnes i din browser.");
      URL.revokeObjectURL(url);
    };
    nextImage.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const maxZoom = useMemo(
    () => image ? cropZoomLimit(image.naturalWidth, image.naturalHeight) : 1,
    [image],
  );
  const baseScale = image ? Math.max(viewport / image.naturalWidth, viewport / image.naturalHeight) : 1;

  const updateZoom = (value) => {
    if (!image) return;
    const nextZoom = Math.max(1, Math.min(maxZoom, Number(value)));
    setZoom(nextZoom);
    setPosition((current) => clampCropPosition(
      current,
      image.naturalWidth,
      image.naturalHeight,
      viewport,
      nextZoom,
    ));
  };

  const pointerDown = (event) => {
    if (!image) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, start: position };
  };

  const pointerMove = (event) => {
    if (!image || drag.current?.pointerId !== event.pointerId) return;
    setPosition(clampCropPosition(
      {
        x: drag.current.start.x + event.clientX - drag.current.x,
        y: drag.current.start.y + event.clientY - drag.current.y,
      },
      image.naturalWidth,
      image.naturalHeight,
      viewport,
      zoom,
    ));
  };

  const finish = async () => {
    if (!image) return;
    setBusy(true);
    setError("");
    try {
      const rect = cropSourceRect(
        image.naturalWidth,
        image.naturalHeight,
        viewport,
        zoom,
        position,
      );
      const canvas = document.createElement("canvas");
      canvas.width = AVATAR_OUTPUT_SIZE;
      canvas.height = AVATAR_OUTPUT_SIZE;
      const context = canvas.getContext("2d", { alpha: false });
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);
      context.drawImage(
        image,
        rect.left,
        rect.top,
        rect.size,
        rect.size,
        0,
        0,
        AVATAR_OUTPUT_SIZE,
        AVATAR_OUTPUT_SIZE,
      );
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", 0.88));
      if (!blob) throw new Error("Billedet kunne ikke beskæres");
      await onConfirm({
        file: new File([blob], "profilbillede.webp", { type: "image/webp" }),
        crop: {
          x: Math.round(position.x * 1000) / 1000,
          y: Math.round(position.y * 1000) / 1000,
          zoom: Math.round(zoom * 1000) / 1000,
          source_width: image.naturalWidth,
          source_height: image.naturalHeight,
          crop_left: Math.round(rect.left * 1000) / 1000,
          crop_top: Math.round(rect.top * 1000) / 1000,
          crop_size: Math.round(rect.size * 1000) / 1000,
          output_size: AVATAR_OUTPUT_SIZE,
        },
      });
    } catch (cropError) {
      setError(cropError.message || "Billedet kunne ikke beskæres");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl border border-brand-line bg-brand-wash p-4 sm:p-5" data-testid="profile-photo-cropper">
      <div className="text-center">
        <h3 className="font-display text-lg font-semibold text-brand-ink">Tilpas profilbilledet</h3>
        <p className="mt-1 text-xs text-brand-muted">Træk billedet for at placere ansigtet. Brug zoom til at vælge udsnittet.</p>
      </div>
      <div ref={preview} className="mx-auto mt-4 aspect-square w-[min(78vw,320px)] overflow-hidden rounded-full border-4 border-white bg-brand-line shadow-sm touch-none relative"
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={() => { drag.current = null; }}
        onPointerCancel={() => { drag.current = null; }}
        role="img"
        aria-label="Cirkulær forhåndsvisning af profilbilledet"
      >
        {image && <img
          src={image.src}
          alt=""
          draggable="false"
          className="pointer-events-none absolute left-1/2 top-1/2 max-w-none select-none"
          style={{
            width: image.naturalWidth * baseScale,
            height: image.naturalHeight * baseScale,
            transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          }}
        />}
        {!image && !error && <div className="flex h-full items-center justify-center text-sm text-brand-muted">Indlæser billede…</div>}
      </div>
      {image && <div className="mx-auto mt-4 flex max-w-sm items-center gap-3">
        <ZoomOut size={17} className="shrink-0 text-brand-body"/>
        <input type="range" min="1" max={maxZoom} step="0.01" value={zoom} onChange={(event) => updateZoom(event.target.value)} className="w-full" aria-label="Zoom profilbillede"/>
        <ZoomIn size={17} className="shrink-0 text-brand-body"/>
      </div>}
      <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-brand-muted"><Move size={13}/> Træk for at flytte udsnittet</div>
      {error && <div className="mt-3 text-center text-sm text-[#991B1B]" role="alert">{error}</div>}
      <div className="mt-4 flex justify-center gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Annuller</button>
        <button type="button" onClick={finish} disabled={!image || busy} className="btn-primary disabled:opacity-50">{busy ? "Gemmer…" : "Brug dette udsnit"}</button>
      </div>
    </div>
  );
}

